// src/components/admin/AdminPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  deleteDoc, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  where, 
  writeBatch,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import AdminToolbar from './AdminToolbar';
import RecordModal from './RecordModal';
import ConfirmationModal from './ConfirmationModal';
import Loading from './Loading';
import ErrorBoundary from './ErrorBoundary';
import UserManagement from './UserManagement';
import LogsViewer from './LogsViewer';
import TokenizationTest from '../../pages/TokenizationTest';
import ApiKeyManagement from './ApiKeyManagement';
import { useAuth } from '../../contexts/AuthContext';
import { logAdminEvent, LOG_LEVELS } from '../../services/loggingService';
import { Database, Users, PieChart, RefreshCw, CreditCard, Zap, Key } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Payments from '../Payments';


const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_COLLECTION = 'sanctions';

const AdminPage = () => {
  const { currentUser, userRole } = useAuth();
  const [authLoading, setAuthLoading] = useState(true);
  
  // New state for active tab
  const [activeTab, setActiveTab] = useState('database');
  
  // State for data processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResult, setProcessingResult] = useState(null);

  // State for payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Function to reprocess sanctions data
  const reprocessSanctionsData = async () => {
    setIsProcessing(true);
    setProcessingResult(null);
    
    try {
      const functions = getFunctions();
      const reprocessFunction = httpsCallable(functions, 'reprocessAllSanctionsData');
      
      // Log the start of the operation
      await logAdminEvent({
        action: 'REPROCESS_SANCTIONS_START',
        level: LOG_LEVELS.INFO,
        details: 'Started reprocessing sanctions data'
      });
      
      const result = await reprocessFunction({});
      console.log("Processing complete:", result.data);
      
      // Log the successful completion
      await logAdminEvent({
        action: 'REPROCESS_SANCTIONS_COMPLETE',
        level: LOG_LEVELS.INFO,
        details: `Successfully processed ${result.data.totalDocumentsProcessed} records`,
        meta: { recordsProcessed: result.data.totalDocumentsProcessed }
      });
      
      setProcessingResult({
        success: true,
        message: `Successfully processed ${result.data.totalDocumentsProcessed} sanctions records`,
        details: result.data
      });
    } catch (error) {
      console.error("Error processing sanctions data:", error);
      
      // Log the error
      await logAdminEvent({
        action: 'REPROCESS_SANCTIONS_ERROR',
        level: LOG_LEVELS.ERROR,
        details: `Error: ${error.message}`,
        meta: { errorMessage: error.message }
      });
      
      setProcessingResult({
        success: false,
        message: `Error: ${error.message}`,
        details: error
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Check admin status when component mounts
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (userRole && userRole.isAdmin) {
          // User is already confirmed as admin through AuthContext
          setAuthLoading(false);
        } else {
          // Double-check with Firestore directly as a fallback
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists() && userDoc.data().isAdmin) {
            setAuthLoading(false);
          } else {
            // Redirect non-admin users
            window.location.href = '/';
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        // Redirect on error
        window.location.href = '/';
      }
    };
    
    if (currentUser) {
      checkAdminStatus();
    } else {
      // Redirect unauthenticated users
      window.location.href = '/';
    }
  }, [currentUser, userRole]);
  
  // State for data management
  const [records, setRecords] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(DEFAULT_COLLECTION);
  const [availableCollections, setAvailableCollections] = useState([DEFAULT_COLLECTION, 'financial-sanctions']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [lastDoc, setLastDoc] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  
  // State for sorting and filtering
  const [sortField, setSortField] = useState('importDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [filterOperator, setFilterOperator] = useState('==');
  
  // State for selection and operations
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // State for modals
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ type: '', message: '' });
  
  // State for available fields 
  const [availableFields, setAvailableFields] = useState([]);

  // Load collections and set up initial state
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        // This is a simplified approach. In a production app, you might
        // want to fetch the actual list of collections from Firebase
        const collections = [
          'sanctions',
          'financial-sanctions',
          'travel-bans',
          'entities'
        ];
        setAvailableCollections(collections);
      } catch (err) {
        console.error("Error fetching collections:", err);
        setError("Failed to load collections");
      }
    };
    
    fetchCollections();
  }, []);
  
  // Load records based on current pagination, sorting, and filtering
  const fetchRecords = useCallback(async (resetPagination = false) => {
    if (activeTab !== 'database') return; // Skip if not on database tab
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching records from ${selectedCollection}, page: ${resetPagination ? '1' : page}, pageSize: ${pageSize}`);
      
      const collectionRef = collection(db, selectedCollection);
      
      // Build query with filters, sorting, and pagination
      // Start with a basic query
      let baseQuery = query(collectionRef);
      
      // Add filter if provided
      if (filterField && filterValue) {
        try {
          // Handle different filter operators
          switch (filterOperator) {
            case '==':
              baseQuery = query(baseQuery, where(filterField, '==', filterValue));
              break;
            case 'contains':
              if (filterField === 'names' || filterField === 'birthdates' || filterField === 'citizenships') {
                // These are arrays so we use array-contains
                baseQuery = query(baseQuery, where(filterField, 'array-contains', filterValue));
              } else {
                // Skip the filter if it's not applicable to avoid errors
                console.warn(`Contains operator not supported for field: ${filterField}`);
              }
              break;
            case '>':
              baseQuery = query(baseQuery, where(filterField, '>', filterValue));
              break;
            case '<':
              baseQuery = query(baseQuery, where(filterField, '<', filterValue));
              break;
            default:
              baseQuery = query(baseQuery, where(filterField, '==', filterValue));
          }
        } catch (filterErr) {
          console.error("Error applying filter:", filterErr);
          setError(`Filter error: ${filterErr.message}. Try a different field or operator.`);
          // Continue without the filter
        }
      }
      
      // Add sorting if possible (only on top-level fields)
      let finalQuery;
      try {
        if (sortField) {
          finalQuery = query(baseQuery, orderBy(sortField, sortDirection));
        } else {
          finalQuery = baseQuery;
        }
      } catch (sortErr) {
        console.error("Error applying sort:", sortErr);
        finalQuery = baseQuery; // Fallback to unsorted query
      }
      
      // Add pagination
      if (!resetPagination && lastDoc) {
        finalQuery = query(finalQuery, startAfter(lastDoc), limit(pageSize));
      } else {
        finalQuery = query(finalQuery, limit(pageSize));
      }
      
      // Execute the query
      const querySnapshot = await getDocs(finalQuery);
      console.log(`Fetched ${querySnapshot.size} records`);
      
      // Check if we've reached the end
      const hasMoreRecords = querySnapshot.size === pageSize;
      setHasMore(hasMoreRecords);
      console.log(`Has more records: ${hasMoreRecords}`);
      
      // Set last document for pagination
      if (querySnapshot.size > 0) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } else {
        setLastDoc(null);
      }
      
      // Process and format the data
      const fetchedRecords = [];
      querySnapshot.forEach((doc) => {
        try {
          const data = doc.data();
          
          // Safely extract values with error handling
          const recordData = {
            id: doc.id,
            ...data,
            // Format complex fields for display with safety checks
            names: Array.isArray(data.names) ? `${data.names.length} name(s)` : "No names",
            birthdates: Array.isArray(data.birthdates) ? `${data.birthdates.length} date(s)` : "No dates",
            citizenships: Array.isArray(data.citizenships) 
              ? data.citizenships.map(c => c?.countryDescription || 'Unknown').join(', ') 
              : "Unknown"
          };
          
          fetchedRecords.push(recordData);
          
          // Extract available fields from the first record
          if (fetchedRecords.length === 1 && resetPagination) {
            const fields = Object.keys(data);
            setAvailableFields(fields);
          }
        } catch (recordErr) {
          console.error(`Error processing record ${doc.id}:`, recordErr);
          // Still add a simplified version of the record
          fetchedRecords.push({
            id: doc.id,
            error: "Error processing record data"
          });
        }
      });
      
      // Update records state based on pagination mode
      if (resetPagination) {
        setRecords(fetchedRecords);
      } else {
        setRecords(prevRecords => [...prevRecords, ...fetchedRecords]);
      }
      
    } catch (err) {
      console.error("Error fetching records:", err);
      setError(`Failed to load records: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedCollection, lastDoc, pageSize, sortField, sortDirection, filterField, filterValue, filterOperator]);
  
  // Initial data fetch
  useEffect(() => {
    if (activeTab === 'database') {
      setLastDoc(null);
      if (page !== 1) setPage(1);
      else fetchRecords(true);
    }
    // eslint-disable-next-line
  }, [activeTab, selectedCollection, sortField, sortDirection, filterField, filterValue, filterOperator]);

  // Fetch records when page is set to 1 (after initial reset)
  useEffect(() => {
    if (activeTab === 'database' && page === 1) {
      fetchRecords(true);
    }
    // eslint-disable-next-line
  }, [page]);

  // Load more records when page > 1
  useEffect(() => {
    if (activeTab === 'database' && page > 1) {
      fetchRecords(false);
    }
    // eslint-disable-next-line
  }, [page]);
  
  // Handle collection change
  const handleCollectionChange = (collection) => {
    setSelectedCollection(collection);
    setLastDoc(null);
    setRecords([]);
    setSelectedRecords([]);
    setSelectAll(false);
  };
  
  // Handle sorting change
  const handleSortChange = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset pagination when sorting changes
    setLastDoc(null);
    setRecords([]);
  };
  
  // Handle filter application
  const handleApplyFilter = () => {
    setLastDoc(null);
    setRecords([]);
    fetchRecords(true);
  };
  
  // Handle filter reset
  const handleResetFilter = () => {
    setFilterField('');
    setFilterValue('');
    setFilterOperator('==');
    setLastDoc(null);
    setRecords([]);
    fetchRecords(true);
  };
  
  // Handle record selection
  const handleSelectRecord = (id) => {
    setSelectedRecords(prev => {
      if (prev.includes(id)) {
        return prev.filter(recordId => recordId !== id);
      } else {
        return [...prev, id];
      }
    });
  };
  
  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(record => record.id));
    }
    setSelectAll(!selectAll);
  };
  
  // Handle viewing a record
  const handleViewRecord = async (id) => {
    try {
      setLoading(true);
      const docRef = doc(db, selectedCollection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setCurrentRecord({
          id: docSnap.id,
          ...docSnap.data()
        });
        setShowRecordModal(true);
      } else {
        setError("Record not found");
      }
    } catch (err) {
      console.error("Error fetching record:", err);
      setError(`Failed to fetch record: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle deleting a record
  const handleDeleteRecord = async (id) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the record with ID: ${id}?`,
      callback: async () => {
        try {
          setLoading(true);
          await deleteDoc(doc(db, selectedCollection, id));
          setRecords(records.filter(record => record.id !== id));
          setSelectedRecords(selectedRecords.filter(recordId => recordId !== id));
          setShowConfirmModal(false);
        } catch (err) {
          console.error("Error deleting record:", err);
          setError(`Failed to delete record: ${err.message}`);
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };
  
  // Handle deleting multiple records
  const handleDeleteSelected = () => {
    if (selectedRecords.length === 0) return;
    
    setConfirmAction({
      type: 'deleteSelected',
      message: `Are you sure you want to delete ${selectedRecords.length} selected records?`,
      callback: async () => {
        try {
          setLoading(true);
          const batch = writeBatch(db);
          
          // Add delete operations to batch
          selectedRecords.forEach(id => {
            const docRef = doc(db, selectedCollection, id);
            batch.delete(docRef);
          });
          
          // Commit the batch
          await batch.commit();
          
          // Update local state
          setRecords(records.filter(record => !selectedRecords.includes(record.id)));
          setSelectedRecords([]);
          setSelectAll(false);
          setShowConfirmModal(false);
        } catch (err) {
          console.error("Error deleting records:", err);
          setError(`Failed to delete records: ${err.message}`);
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };
  
  // Handle clearing the collection
  const handleClearCollection = () => {
    setConfirmAction({
      type: 'clearCollection',
      message: `WARNING: This will delete ALL records in the "${selectedCollection}" collection. This action cannot be undone. Are you absolutely sure?`,
      callback: async () => {
        try {
          setLoading(true);
          
          // Get all documents in the collection
          const collectionRef = collection(db, selectedCollection);
          const snapshot = await getDocs(collectionRef);
          
          // Process in batches of 500 (Firestore batch limit)
          const batchSize = 500;
          const batches = [];
          let currentBatch = writeBatch(db);
          let operationCount = 0;
          
          snapshot.forEach((document) => {
            currentBatch.delete(doc(db, selectedCollection, document.id));
            operationCount++;
            
            if (operationCount >= batchSize) {
              batches.push(currentBatch.commit());
              currentBatch = writeBatch(db);
              operationCount = 0;
            }
          });
          
          // Commit any remaining operations
          if (operationCount > 0) {
            batches.push(currentBatch.commit());
          }
          
          // Execute all batches
          await Promise.all(batches);
          
          // Update state
          setRecords([]);
          setSelectedRecords([]);
          setSelectAll(false);
          setLastDoc(null);
          setHasMore(false);
          setShowConfirmModal(false);
        } catch (err) {
          console.error("Error clearing collection:", err);
          setError(`Failed to clear collection: ${err.message}`);
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };
  
  // Render the admin interface
  return authLoading ? (
    <div className="flex justify-center items-center h-screen">
      <Loading message="Checking authentication..." />
    </div>
  ) : (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
      
      {/* Tabs for different admin sections */}
      <div className="mb-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex flex-wrap -mb-px">
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'database'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('database')}
          >
            <div className="flex items-center">
              <Database size={18} className="mr-2" />
              Database
            </div>
          </button>
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'users'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('users')}
          >
            <div className="flex items-center">
              <Users size={18} className="mr-2" />
              Users
            </div>
          </button>
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'apiKeys'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('apiKeys')}
          >
            <div className="flex items-center">
              <Key size={18} className="mr-2" />
              API Keys
            </div>
          </button>
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'stats'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('stats')}
          >
            <div className="flex items-center">
              <PieChart size={18} className="mr-2" />
              Stats
            </div>
          </button>
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'logs'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('logs')}
          >
            <div className="flex items-center">
              <RefreshCw size={18} className="mr-2" />
              Logs
            </div>
          </button>
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'payments'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('payments')}
          >
            <div className="flex items-center">
              <CreditCard size={18} className="mr-2" />
              Payments
            </div>
          </button>
          <button
            className={`inline-block p-4 rounded-t-lg border-b-2 font-medium text-sm mr-2 ${
              activeTab === 'tokenization'
                ? 'border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500'
                : 'border-transparent text-slate-500 hover:text-slate-600 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('tokenization')}
          >
            <div className="flex items-center">
              <Zap size={18} className="mr-2" />
              Name Tokenization
            </div>
          </button>
        </div>
      </div>
      
      {/* Content for the selected tab */}
      <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow">
        {activeTab === 'database' && (
          <ErrorBoundary>
            {/* Add reprocess functionality */}
            <div className="mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold mb-2">Sanctions Data Management</h2>
              <div className="flex flex-wrap gap-2">
                <button 
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors duration-200 flex items-center"
                  onClick={reprocessSanctionsData}
                  disabled={isProcessing}
                >
                  <RefreshCw size={16} className={`mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                  {isProcessing ? 'Processing...' : 'Reprocess Sanctions Data'}
                </button>
              </div>
              
              {processingResult && (
                <div className={`mt-4 p-4 rounded-md ${processingResult.success ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
                  <p className="font-medium">{processingResult.message}</p>
                  {processingResult.details && (
                    <pre className="mt-2 text-xs bg-white dark:bg-slate-900 p-2 rounded overflow-auto max-h-40">
                      {JSON.stringify(processingResult.details, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
            
            {/* Database management */}
            <div className="mb-4">
              <AdminToolbar 
                collections={availableCollections}
                selectedCollection={selectedCollection}
                onCollectionChange={handleCollectionChange}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                filterField={filterField}
                filterValue={filterValue}
                filterOperator={filterOperator}
                onFilterFieldChange={setFilterField}
                onFilterValueChange={setFilterValue}
                onFilterOperatorChange={setFilterOperator}
                onApplyFilter={handleApplyFilter}
                onResetFilter={handleResetFilter}
                availableFields={availableFields}
                selectedCount={selectedRecords.length}
                onDeleteSelected={handleDeleteSelected}
                onClearCollection={handleClearCollection}
              />
            </div>
            
            {error && (
              <div className="mb-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700">
                <p>{error}</p>
              </div>
            )}
            
            {loading ? (
              <Loading message={`Loading records from ${selectedCollection}...`} />
            ) : (
              <div>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    <thead className="bg-slate-50 dark:bg-slate-700">
                      <tr>
                        <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600">
                          <input 
                            type="checkbox" 
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="form-checkbox h-5 w-5 text-blue-600"
                          />
                        </th>
                        {Object.keys(records[0] || {}).slice(0, 8).map(field => (
                          <th 
                            key={field}
                            className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                          >
                            {field}
                          </th>
                        ))}
                        <th className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map(record => (
                        <tr key={record.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600">
                            <input 
                              type="checkbox" 
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                              className="form-checkbox h-5 w-5 text-blue-600"
                            />
                          </td>
                          {Object.entries(record).slice(0, 8).map(([key, value]) => (
                            <td 
                              key={key}
                              className="py-2 px-3 border-b border-slate-200 dark:border-slate-600 text-sm"
                            >
                              {typeof value === 'object' ? (
                                <span className="text-slate-500 dark:text-slate-400">
                                  {Array.isArray(value) 
                                    ? `[Array:${value.length}]` 
                                    : '{Object}'}
                                </span>
                              ) : (
                                typeof value === 'boolean' ? 
                                String(value) : 
                                value
                              )}
                            </td>
                          ))}
                          <td className="py-2 px-3 border-b border-slate-200 dark:border-slate-600">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewRecord(record.id)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDeleteRecord(record.id)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {records.length === 0 && (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                    No records found. Try a different collection or reset filters.
                  </div>
                )}
                
                {hasMore && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setPage(prevPage => prevPage + 1)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            )}
          </ErrorBoundary>
        )}
        
        {activeTab === 'users' && (
          <ErrorBoundary>
            <UserManagement />
          </ErrorBoundary>
        )}
        
        {activeTab === 'apiKeys' && (
          <ErrorBoundary>
            <ApiKeyManagement />
          </ErrorBoundary>
        )}
        
        {activeTab === 'stats' && (
          <ErrorBoundary>
            <div>
              <h2 className="text-lg font-semibold mb-4">Statistics Dashboard</h2>
              <p className="text-slate-600 dark:text-slate-400">
                Statistics feature coming soon...
              </p>
            </div>
          </ErrorBoundary>
        )}
        
        {activeTab === 'logs' && (
          <ErrorBoundary>
            <LogsViewer />
          </ErrorBoundary>
        )}
        
        {activeTab === 'payments' && (
          <ErrorBoundary>
            <Payments />
          </ErrorBoundary>
        )}
        
        {activeTab === 'tokenization' && (
          <ErrorBoundary>
            <TokenizationTest />
          </ErrorBoundary>
        )}
      </div>
      
      {/* Record Modal */}
      {showRecordModal && (
        <RecordModal
          record={currentRecord}
          onClose={() => setShowRecordModal(false)}
        />
      )}
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmationModal
          message={confirmAction.message}
          onConfirm={() => {
            if (confirmAction.type === 'delete') {
              handleDeleteRecord(confirmAction.id);
            } else if (confirmAction.type === 'deleteSelected') {
              handleDeleteSelected();
            } else if (confirmAction.type === 'clearCollection') {
              handleClearCollection();
            }
            setShowConfirmModal(false);
          }}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
      
      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6 max-w-lg w-full">
            <h2 className="text-xl font-semibold mb-4">Payment Settings</h2>
            <Payments isModal={true} />
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 bg-slate-300 hover:bg-slate-400 text-slate-800 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;